// apps/merchant-v3/src/layouts/TopBar.tsx
//
// Phase 6 Wave 1 Task 02 — TopBar.
// Phase 6 amend #10 (dzung-order redesign Image #5) — full layout
// overhaul. Slots, left → right:
//   1. Logo (printway.io)
//   2. Vertical divider
//   3. Cutoff Time pill (dismissible, ⓘ 1/3 pager)
//   4. flex spacer
//   5. Email-not-verified call-out chip (orange, conditional)
//   6. Wallet chip (BillingWidget — $ Wallet $0.00 ▼)
//   7. Notification bell
//   8. Language selector (flag + EN ▼)
//   9. Timezone selector (clock + GMT+7:00 ▼)
//  10. Help dropdown (? Help ▼)
//  11. Profile dropdown (avatar + Account + tier ▼)
//
// Breadcrumb relocated to its own row below the topbar (mounted in
// AppShell) — image #5 shows a single-row topbar with no breadcrumb in
// the bar itself.
//
// vg-binding: PLAN:task-02
// vg-binding: PLAN:task-42
// vg-binding: P6.D-05
// vg-binding: P6.D-06
// vg-binding: P6.D-114
// vg-binding: P6.D-245
// vg-binding: P6.D-246
// vg-binding: P6.D-247
// vg-binding: P6.D-248
// vg-binding: G-70
// vg-binding: G-71

import { Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { BillingWidget } from '@/components/topbar/BillingWidget';
import { LanguageSelector } from '@/components/LanguageSelector';
import { TimezoneSelector } from '@/components/TimezoneSelector';
import { NotificationBell } from '@/components/NotificationBell';
import { ProfileDropdown } from '@/components/ProfileDropdown';
import { AnnouncementCarousel } from '@/components/topbar/AnnouncementCarousel';
import { EmailVerifyChip } from '@/components/topbar/EmailVerifyChip';
import { HelpDropdown } from '@/components/topbar/HelpDropdown';
import { useSidebarStore } from '@/store/sidebar';

export function TopBar(): JSX.Element {
  const { t } = useTranslation('common');
  const setMobileOpen = useSidebarStore((s) => s.setMobileOpen);
  return (
    <header
      role="banner"
      className="flex h-[56px] w-full min-w-0 shrink-0 items-center gap-3 overflow-hidden border-b border-pw-border bg-pw-card px-3 sm:px-4"
    >
      {/* Hamburger — opens the off-canvas nav drawer. lg+ shows the fixed
          sidebar instead, so the trigger disappears there. */}
      <button
        type="button"
        data-testid="topbar-mobile-nav-trigger"
        aria-label={t('a11y.openNavigation')}
        onClick={() => setMobileOpen(true)}
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-pw-text-muted hover:bg-pw-surface hover:text-pw-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 lg:hidden"
      >
        <Menu className="h-5 w-5" aria-hidden />
      </button>

      {/* Left — announcement carousel (Phase 8.3 amend #2).
          Replaces the hardcoded Cutoff Time pill — now fetches from
          GET /api/v1/announcements?portal=merchant&surface=topbar and
          rotates through every active announcement. Hidden on phones where
          the right-side chips already fill the 56px bar. */}
      <div className="hidden min-w-0 sm:contents">
        <AnnouncementCarousel />
      </div>

      {/* Spacer */}
      <div className="flex-1" aria-hidden />

      {/* Right — chips + selectors. The bar is overflow-hidden, so on narrow
          viewports the low-priority selectors are dropped (md:contents keeps
          them out of the box tree at md+, no extra flex gap) instead of
          clipping arbitrarily mid-chip. */}
      <div className="flex shrink-0 items-center gap-1 sm:gap-2">
        <div className="hidden sm:contents">
          <EmailVerifyChip />
        </div>
        <BillingWidget />
        <NotificationBell />
        <div className="hidden md:contents">
          <LanguageSelector />
          <TimezoneSelector />
          <HelpDropdown />
        </div>
        <ProfileDropdown />
      </div>
    </header>
  );
}

export default TopBar;
